import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

interface Props {
  initialStatus?: boolean
  disabled?: boolean
  children: React.ReactNode | ((props: ToggleContext) => React.ReactNode)
}

export default function WithToggle({
  initialStatus = false,
  disabled = false,
  children,
}: Props) {
  const [isActive, setIsActive] = useState<boolean>(initialStatus)

  const open = useCallback(_open, [disabled])
  const close = useCallback(_close, [disabled])
  const toggle = useCallback(_toggle, [disabled])

  const value = {
    isActive,
    open,
    close,
    toggle,
  }

  useEffect(() => {
    disabled && setIsActive(false)
  }, [disabled])

  return (
    <toggleContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </toggleContext.Provider>
  )

  function _open() {
    if (disabled) {
      return
    }

    setIsActive(true)
  }

  function _close() {
    if (disabled) {
      return
    }

    setIsActive(false)
  }

  function _toggle(nextState?: boolean) {
    if (disabled) {
      return
    }

    setIsActive((prev) => (nextState === undefined ? !prev : nextState))
  }
}

export const toggleContext = createContext<ToggleContext | null>(null)

export function useToggleContext() {
  return useContext(toggleContext)
}

export interface ToggleContext {
  isActive: boolean
  open(): void
  close(): void
  toggle(next?: boolean): void
}
